import { useMemo, useState } from "react";
import { Link } from "react-router";
import { css } from "styled-system/css";
import { InlineBar } from "~/components/charts";
import type { PriorSnapshot, RosterPerson } from "../types";
import { fmtHours, fmtNum } from "~/lib/format";

type Num = (p: RosterPerson) => number | null;
type PrevNum = (p: PriorSnapshot) => number | null;

interface Column {
  key: string;
  label: string;
  title: string;
  get: Num;
  prev?: PrevNum;
  format?: (v: number | null) => string;
  bar?: boolean;
  /** Which direction is better, for the tint rule. */
  better?: "up" | "down";
}

const columns: Column[] = [
  {
    key: "score",
    label: "Score",
    title: "0 to 100: delivery 40, durability 35, reviewing others 25, against the org's 90th percentile",
    get: (p) => p.score,
    prev: (p) => p.score,
  },
  {
    key: "prsMerged",
    label: "Merged",
    title: "PRs they authored that merged in the period, by merge date",
    get: (p) => p.prsMerged,
    prev: (p) => p.prsMerged,
    bar: true,
  },
  {
    key: "prsCreated",
    label: "Opened",
    title: "PRs they opened in the period",
    get: (p) => p.prsCreated,
    prev: (p) => p.prsCreated,
  },
  {
    key: "reviewsGiven",
    label: "Reviewed",
    title: "Distinct PRs by other people they reviewed in the period",
    get: (p) => p.reviewsGiven,
    prev: (p) => p.reviewsGiven,
    bar: true,
  },
  {
    key: "decisiveReviews",
    label: "Decided",
    title: "Of those, PRs where they approved or requested changes",
    get: (p) => p.decisiveReviews,
    prev: (p) => p.decisiveReviews,
  },
  {
    key: "effectiveIssuesCompleted",
    label: "Tickets",
    title: "JIRA issues done in the period, plus issues that moved into code review or QA in it",
    get: (p) => p.effectiveIssuesCompleted,
    prev: (p) => p.effectiveIssuesCompleted,
  },
  {
    key: "totalCommits",
    label: "Commits",
    title: "Authored commits: no merge commits, no squash duplicates",
    get: (p) => p.totalCommits,
    prev: (p) => p.totalCommits,
  },
  {
    key: "aiUsageRate",
    label: "AI %",
    title: "Share of their commits written by or with a coding agent",
    get: (p) => (p.totalCommits > 0 ? p.aiUsageRate : null),
    format: (v) => (v === null ? "–" : `${v}%`),
  },
  {
    key: "medianHoursToMerge",
    label: "To merge",
    title: "Median time from open to merge of their PRs merged in the period",
    get: (p) => p.medianHoursToMerge,
    prev: (p) => p.medianHoursToMerge,
    format: fmtHours,
    better: "down",
  },
  {
    key: "heldUp",
    label: "Held up",
    title: "Share of shipped work that did not come back (reverts, follow-up fixes, QA rejections, bugs, abandoned PRs). Shown from 3 merged PRs.",
    get: (p) =>
      p.scoreBreakdown.failureRateMeasured
        ? Math.round(100 - p.scoreBreakdown.failureRate)
        : null,
    format: (v) => (v === null ? "–" : `${v}%`),
  },
];

function deltaText(cur: number | null, prev: number | null | undefined, hours = false) {
  if (cur === null || prev === null || prev === undefined) return "";
  const d = Math.round((cur - prev) * 10) / 10;
  if (d === 0) return "";
  const arrow = d > 0 ? "▲" : "▼";
  return `${arrow}${hours ? fmtHours(Math.abs(d)) : fmtNum(Math.abs(d))}`;
}

interface RosterTableProps {
  people: RosterPerson[];
  quiet: RosterPerson[];
  priorName: string;
}

export function RosterTable({ people, quiet, priorName }: RosterTableProps) {
  const [sortKey, setSortKey] = useState<string>("score");
  const [asc, setAsc] = useState(false);
  const [query, setQuery] = useState("");
  const [showQuiet, setShowQuiet] = useState(false);

  const rows = useMemo(() => {
    const pool = showQuiet ? [...people, ...quiet] : people;
    const q = query.trim().toLowerCase();
    const filtered = q
      ? pool.filter(
          (p) =>
            p.displayName.toLowerCase().includes(q) ||
            (p.githubUsername ?? "").toLowerCase().includes(q) ||
            p.teams.some((t) => t.name.toLowerCase().includes(q)),
        )
      : pool;
    const col = columns.find((c) => c.key === sortKey);
    const get: (p: RosterPerson) => number | string | null =
      sortKey === "displayName" ? (p) => p.displayName : (col?.get ?? ((p) => p.score));
    return [...filtered].sort((a, b) => {
      const av = get(a);
      const bv = get(b);
      if (av === null && bv === null) return 0;
      if (av === null) return 1;
      if (bv === null) return -1;
      const cmp =
        typeof av === "string" ? av.localeCompare(String(bv)) : (av as number) - (bv as number);
      return asc ? cmp : -cmp;
    });
  }, [people, quiet, showQuiet, query, sortKey, asc]);

  const maxOf = useMemo(() => {
    const m: Record<string, number> = {};
    for (const c of columns.filter((c) => c.bar)) {
      m[c.key] = Math.max(
        1,
        ...people.map((p) => Math.max(c.get(p) ?? 0, p.previous && c.prev ? (c.prev(p.previous) ?? 0) : 0)),
      );
    }
    return m;
  }, [people]);

  const sortBy = (key: string) => {
    if (key === sortKey) setAsc(!asc);
    else {
      setSortKey(key);
      setAsc(key === "displayName" || key === "medianHoursToMerge");
    }
  };

  return (
    <div>
      <div className={styles.controls}>
        <input
          type="search"
          placeholder="Find a person or team"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className={styles.search}
          aria-label="Find a person or team"
        />
        <span className={styles.count}>
          {fmtNum(rows.length)} of {fmtNum(people.length + (showQuiet ? quiet.length : 0))}
        </span>
        {quiet.length > 0 && (
          <label className={styles.toggle}>
            <input
              type="checkbox"
              checked={showQuiet}
              onChange={(e) => setShowQuiet(e.target.checked)}
            />
            Include {fmtNum(quiet.length)} active in {priorName} only
          </label>
        )}
      </div>
      <div className={styles.scroll}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.rank}>#</th>
              <th
                className={styles.nameTh}
                aria-sort={sortKey === "displayName" ? (asc ? "ascending" : "descending") : "none"}
              >
                <button type="button" onClick={() => sortBy("displayName")}>
                  Person
                </button>
              </th>
              {columns.map((c) => (
                <th
                  key={c.key}
                  title={c.title}
                  data-active={sortKey === c.key || undefined}
                  aria-sort={sortKey === c.key ? (asc ? "ascending" : "descending") : "none"}
                >
                  <button type="button" onClick={() => sortBy(c.key)}>
                    {c.label}
                    {sortKey === c.key ? (asc ? " ↑" : " ↓") : ""}
                  </button>
                </th>
              ))}
              <th className={styles.repoTh}>Top repo</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p, i) => (
              <tr key={p.id} data-quiet={!p.activeThisPeriod || undefined}>
                <td className={styles.rank}>{i + 1}</td>
                <td className={styles.name}>
                  <Link to={`/developers/${p.id}`}>{p.displayName}</Link>
                  <span className={styles.sub}>
                    {p.teams[0]?.name ?? (p.githubUsername ? `@${p.githubUsername}` : "")}
                    {!p.isLinkedProfile && <span title="Built from a git commit email with no GitHub account linked"> · no GitHub link</span>}
                    {!p.activeLastPeriod && p.activeThisPeriod && <span> · new</span>}
                  </span>
                </td>
                {columns.map((c) => {
                  const v = c.get(p);
                  const prev = p.previous && c.prev ? c.prev(p.previous) : null;
                  const fmt = c.format ?? ((x: number | null) => fmtNum(x));
                  const flagged =
                    c.key === "heldUp" &&
                    p.scoreBreakdown.failureRateMeasured &&
                    p.scoreBreakdown.orgFailureRate > 0 &&
                    p.scoreBreakdown.failureRate >= 2 * p.scoreBreakdown.orgFailureRate;
                  return (
                    <td
                      key={c.key}
                      data-flag={flagged || undefined}
                      title={
                        flagged
                          ? `At least twice the org rate: ${p.revertedPrs} reverted, ${p.followUpFixes} follow-up fixes, ${p.qaRejections} QA rejections, ${p.causedBugs} bugs, ${p.abandonedPrs} abandoned`
                          : undefined
                      }
                    >
                      <span className={styles.val} data-zero={v === 0 || v === null || undefined}>
                        {fmt(v)}
                      </span>
                      {c.prev && (
                        <span className={styles.delta}>
                          {p.previous
                            ? deltaText(v, prev, c.key === "medianHoursToMerge")
                            : p.activeThisPeriod && (v ?? 0) > 0
                              ? "new"
                              : ""}
                        </span>
                      )}
                      {c.bar && (
                        <InlineBar value={v ?? 0} prior={prev} max={maxOf[c.key] ?? 1} width={44} />
                      )}
                    </td>
                  );
                })}
                <td className={styles.repo}>{p.topRepo ?? ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const styles = {
  controls: css({
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "8px 16px",
    py: "8px",
    fontFamily: "sans",
    fontSize: "12px",
    color: "ed.ink2",
  }),
  search: css({
    fontFamily: "sans",
    fontSize: "13px",
    color: "ed.ink",
    bg: "transparent",
    border: "none",
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderColor: "ed.ink",
    borderRadius: 0,
    px: 0,
    py: "4px",
    width: "220px",
    outline: "none",
    _placeholder: { color: "ed.ink3" },
  }),
  count: css({ fontFamily: "mono", fontVariantNumeric: "tabular-nums", color: "ed.ink3" }),
  toggle: css({
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    cursor: "pointer",
    "& input": { accentColor: "var(--colors-ed-ink)" },
  }),
  scroll: css({ overflowX: "auto", borderTopWidth: "2px", borderTopStyle: "solid", borderColor: "ed.ink" }),
  table: css({
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "12px",
    "& th": {
      fontFamily: "sans",
      fontSize: "10px",
      fontWeight: 700,
      letterSpacing: "0.07em",
      textTransform: "uppercase",
      color: "ed.ink3",
      textAlign: "right",
      whiteSpace: "nowrap",
      py: "6px",
      px: "6px",
      borderBottomWidth: "1px",
      borderBottomStyle: "solid",
      borderColor: "ed.ink",
      verticalAlign: "bottom",
    },
    "& th button": {
      all: "unset",
      cursor: "pointer",
    },
    // Spelled out: a nested _hover here would fire on hovering the table
    "& th button:hover": { color: "ed.ink" },
    "& th button:focus-visible": { outline: "1px solid var(--colors-ed-ink)" },
    "& th[data-active]": { color: "ed.ink" },
    "& td": {
      fontFamily: "mono",
      fontVariantNumeric: "tabular-nums",
      textAlign: "right",
      whiteSpace: "nowrap",
      py: "4px",
      px: "6px",
      borderBottomWidth: "1px",
      borderBottomStyle: "solid",
      borderColor: "ed.grid",
      color: "ed.ink",
    },
    "& td[data-flag]": { bg: "ed.cautionTint" },
    "& tbody tr:hover td": { bg: "ed.panel" },
    "& tr[data-quiet] td": { color: "ed.ink3" },
  }),
  rank: css({ color: "ed.ink3", textAlign: "right!", width: "28px" }),
  nameTh: css({ textAlign: "left!" }),
  repoTh: css({ textAlign: "left!" }),
  name: css({
    textAlign: "left!",
    fontFamily: "sans!",
    "& a": { fontWeight: 600, textDecoration: "none" },
    "& a:hover": { textDecoration: "underline" },
  }),
  sub: css({ ml: "6px", fontSize: "10.5px", color: "ed.ink3", fontWeight: 400 }),
  val: css({ "&[data-zero]": { color: "ed.ink3" } }),
  delta: css({
    display: "inline-block",
    minWidth: "34px",
    ml: "4px",
    fontSize: "10.5px",
    color: "ed.ink3",
    textAlign: "left",
  }),
  repo: css({ textAlign: "left!", fontSize: "11px!", color: "ed.ink2!" }),
};
